/**
 * Accessibility utility functions and constants
 * Provides consistent ARIA labels, keyboard navigation, and WCAG compliance
 */

// Keyboard codes for consistent handling
export const KEYS = {
  ENTER: "Enter",
  SPACE: " ",
  ESCAPE: "Escape",
  TAB: "Tab",
  ARROW_UP: "ArrowUp",
  ARROW_DOWN: "ArrowDown",
  ARROW_LEFT: "ArrowLeft",
  ARROW_RIGHT: "ArrowRight",
  HOME: "Home",
  END: "End",
  PAGE_UP: "PageUp",
  PAGE_DOWN: "PageDown",
};

// WCAG compliant color contrast ratios
export const CONTRAST_RATIOS = {
  AA_NORMAL: 4.5,
  AA_LARGE: 3,
  AAA_NORMAL: 7,
  AAA_LARGE: 4.5,
};

// Standard ARIA labels for common UI elements
export const ARIA_LABELS = {
  // Navigation
  MAIN_NAVIGATION: "Main navigation",
  BREADCRUMB: "Breadcrumb navigation",
  PAGINATION: "Pagination navigation",

  // Buttons
  CLOSE: "Close",
  MENU: "Open menu",
  SEARCH: "Search",
  FILTER: "Filter options",
  SORT: "Sort options",
  MORE_OPTIONS: "More options",
  PREVIOUS: "Previous",
  NEXT: "Next",

  // Form elements
  REQUIRED_FIELD: "Required field",
  OPTIONAL_FIELD: "Optional field",
  ERROR_MESSAGE: "Error message",
  SUCCESS_MESSAGE: "Success message",
  WARNING_MESSAGE: "Warning message",
  INFO_MESSAGE: "Information message",

  // Status indicators
  LOADING: "Loading",
  ONLINE: "Online",
  OFFLINE: "Offline",
  AVAILABLE: "Available",
  BUSY: "Busy",
  AWAY: "Away",

  // Dialogs
  MODAL: "Dialog",
  ALERT: "Alert dialog",
  CONFIRMATION: "Confirmation dialog",
};

// Generate unique IDs for ARIA relationships
export const generateId = (prefix = "aria") => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

// Check if an element is focusable
export const isFocusable = (element) => {
  if (!element) return false;

  const focusableElements = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ];

  return focusableElements.some((selector) => element.matches(selector));
};

// Get all focusable elements within a container
export const getFocusableElements = (container) => {
  if (!container) return [];

  const elements = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );

  return Array.from(elements).filter((el) => {
    // Check if element is visible
    const style = window.getComputedStyle(el);
    return !el.matches(":disabled") && style.display !== "none" && style.visibility !== "hidden";
  });
};

// Only the topmost dialog owns keyboard/focus handling, including sibling portals.
const focusTraps = [];
let previousBodyOverflow;
export const isActiveFocusTrap = (container) =>
  focusTraps[focusTraps.length - 1]?.container === container;

export const trapFocus = (container, { onEscape, restoreFocus = false } = {}) => {
  const document = container.ownerDocument;
  // Child effects run before parent effects when nested dialogs mount together.
  const descendantIndex = focusTraps.findIndex((trap) => container.contains(trap.container));
  const previousActiveElement = descendantIndex < 0
    ? document.activeElement
    : focusTraps[descendantIndex].previousActiveElement;
  const trap = { container, previousActiveElement };
  if (!focusTraps.length) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  if (descendantIndex < 0) focusTraps.push(trap);
  else focusTraps.splice(descendantIndex, 0, trap);

  const enforceBoundary = () => {
    if (!isActiveFocusTrap(container)) return;
    const active = document.activeElement;
    if (!container.contains(active) || active?.matches(":disabled")) {
      container.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.defaultPrevented || !isActiveFocusTrap(container)) return;
    if (e.key === KEYS.ESCAPE) {
      e.preventDefault();
      e.stopPropagation();
      onEscape?.();
      return;
    }
    if (e.key !== KEYS.TAB) return;
    // Resolve on each keypress: pending forms can disable an entire fieldset.
    const focusableElements = getFocusableElements(container);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const active = document.activeElement;
    if (!firstFocusable) {
      e.preventDefault();
      container.focus();
    } else if (active === container || !container.contains(active) || active?.matches(":disabled")) {
      e.preventDefault();
      (e.shiftKey ? lastFocusable : firstFocusable).focus();
    } else if (e.shiftKey && active === firstFocusable) {
      e.preventDefault();
      lastFocusable.focus();
    } else if (!e.shiftKey && active === lastFocusable) {
      e.preventDefault();
      firstFocusable.focus();
    }
  };

  // Native focus can fall to body when a focused submit is disabled. Capture
  // at document level so Tab and outside programmatic focus cannot escape.
  document.addEventListener("keydown", handleKeyDown, true);
  document.addEventListener("focusin", enforceBoundary, true);
  // Blur handlers can change styles while native focus is briefly on body.
  // Do not steal the next field's focus during that in-progress transition.
  let boundaryFrame = null;
  const view = document.defaultView;
  const observer = new MutationObserver(() => {
    if (boundaryFrame !== null) return;
    boundaryFrame = view.requestAnimationFrame(() => {
      boundaryFrame = null;
      enforceBoundary();
    });
  });
  observer.observe(container, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["disabled", "hidden", "style", "class", "tabindex"],
  });
  if (isActiveFocusTrap(container)) container.focus();

  return () => {
    const wasActive = isActiveFocusTrap(container);
    focusTraps.splice(focusTraps.indexOf(trap), 1);
    document.removeEventListener("keydown", handleKeyDown, true);
    document.removeEventListener("focusin", enforceBoundary, true);
    observer.disconnect();
    if (boundaryFrame !== null) view.cancelAnimationFrame(boundaryFrame);
    const next = focusTraps[focusTraps.length - 1]?.container;
    if (!focusTraps.length) document.body.style.overflow = previousBodyOverflow;
    if (wasActive) {
      if (restoreFocus && previousActiveElement?.isConnected &&
          !previousActiveElement.matches(":disabled") &&
          (!next || next.contains(previousActiveElement))) {
        previousActiveElement.focus();
      } else {
        next?.focus();
      }
    }
  };
};

// Announce message to screen readers
export const announce = (message, priority = "polite") => {
  const announcement = document.createElement("div");
  announcement.setAttribute("role", "status");
  announcement.setAttribute("aria-live", priority);
  announcement.setAttribute("aria-atomic", "true");
  announcement.style.position = "absolute";
  announcement.style.left = "-10000px";
  announcement.style.width = "1px";
  announcement.style.height = "1px";
  announcement.style.overflow = "hidden";

  announcement.textContent = message;
  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Handle keyboard navigation for lists/grids
export const handleListKeyboardNavigation = (
  e,
  currentIndex,
  totalItems,
  onChange,
) => {
  let newIndex = currentIndex;

  switch (e.key) {
    case KEYS.ARROW_UP:
      e.preventDefault();
      newIndex = Math.max(0, currentIndex - 1);
      break;
    case KEYS.ARROW_DOWN:
      e.preventDefault();
      newIndex = Math.min(totalItems - 1, currentIndex + 1);
      break;
    case KEYS.HOME:
      e.preventDefault();
      newIndex = 0;
      break;
    case KEYS.END:
      e.preventDefault();
      newIndex = totalItems - 1;
      break;
    default:
      return;
  }

  if (newIndex !== currentIndex) {
    onChange(newIndex);
  }
};

// Check color contrast ratio
export const getContrastRatio = (foreground, background) => {
  // Convert hex to RGB
  const getRGB = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  // Calculate relative luminance
  const getLuminance = (rgb) => {
    const { r, g, b } = rgb;
    const sRGB = [r, g, b].map((val) => {
      val = val / 255;
      return val <= 0.03928
        ? val / 12.92
        : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
  };

  const fgRGB = getRGB(foreground);
  const bgRGB = getRGB(background);

  if (!fgRGB || !bgRGB) return 0;

  const fgLuminance = getLuminance(fgRGB);
  const bgLuminance = getLuminance(bgRGB);

  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

// Check if contrast meets WCAG standards
export const meetsContrastGuidelines = (
  ratio,
  fontSize = 16,
  fontWeight = 400,
) => {
  const isLargeText = fontSize >= 18 || (fontSize >= 14 && fontWeight >= 700);

  return {
    AA:
      ratio >=
      (isLargeText ? CONTRAST_RATIOS.AA_LARGE : CONTRAST_RATIOS.AA_NORMAL),
    AAA:
      ratio >=
      (isLargeText ? CONTRAST_RATIOS.AAA_LARGE : CONTRAST_RATIOS.AAA_NORMAL),
  };
};

// Ensure minimum touch target size (48x48px for mobile)
export const ensureTouchTarget = (width, height) => {
  const MIN_SIZE = 48;
  return {
    width: Math.max(width, MIN_SIZE),
    height: Math.max(height, MIN_SIZE),
    needsPadding: width < MIN_SIZE || height < MIN_SIZE,
  };
};

// Create skip link for keyboard navigation
export const createSkipLink = (targetId, text = "Skip to main content") => {
  const link = document.createElement("a");
  link.href = `#${targetId}`;
  link.className =
    "sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white dark:bg-gray-800 px-4 py-2 rounded-md shadow-lg z-50";
  link.textContent = text;

  link.addEventListener("click", (e) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.tabIndex = -1;
      target.focus();
      target.scrollIntoView({ behavior: "smooth" });
    }
  });

  return link;
};

// Debounce function for live regions
export const debounceAnnouncement = (func, delay = 500) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Format time for screen readers
export const formatTimeForScreenReader = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  if (secs > 0 || parts.length === 0)
    parts.push(`${secs} second${secs !== 1 ? "s" : ""}`);

  return parts.join(", ");
};

export default {
  KEYS,
  CONTRAST_RATIOS,
  ARIA_LABELS,
  generateId,
  isFocusable,
  getFocusableElements,
  trapFocus,
  announce,
  handleListKeyboardNavigation,
  getContrastRatio,
  meetsContrastGuidelines,
  ensureTouchTarget,
  createSkipLink,
  debounceAnnouncement,
  formatTimeForScreenReader,
};
